import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { consultantReports, type ConsultantReportContent } from '../common/consultantReports';
import { consultantReportStyles as styles } from '../common/consultantReportStyles';
import { formatLocalDateTime } from '../utils/localDateTime';
import { printRenderedReport } from '../utils/printRenderedReport';
import AnalysisMarkdown from './reports/AnalysisMarkdown';
import { ReportNavigationContext } from '../common/reportNavigation';

export const ConsultantReportBody: React.FC<{ report: ConsultantReportContent }> = ({ report }) => <article>
    <h1 className="mb-5 text-2xl font-black">{report.title}</h1>
    <AnalysisMarkdown value={report.summary} />
    {report.sections.map((section, index) => <section key={index} className="mt-6">
        <h2 className="mb-3 text-lg font-bold">{section.heading}</h2>
        <AnalysisMarkdown value={section.content} />
    </section>)}
    {!!report.sources.length && <section className="mt-6"><h2 className="mb-3 text-lg font-bold">Sources</h2>
        <ul className="list-inside list-disc space-y-2">{report.sources.map((source, index) => <li key={index}>
            <a href={source.url} target="_blank" rel="noreferrer" className="break-words text-blue-600 underline">{source.sourceName}</a>
        </li>)}</ul></section>}
</article>;

export const ConsultantReportView: React.FC<{ reportId: string; onClose: () => void; inline?: boolean }> = ({ reportId, onClose, inline = false }) => {
    const [result, setResult] = useState<{ report: ConsultantReportContent; serviceName: string; deliveredBy: string; deliveredAt: string } | null>(null);
    const [error, setError] = useState('');
    const body = useRef<HTMLDivElement>(null);
    useEffect(() => {
        let active = true;
        setResult(null);
        setError('');
        void consultantReports<typeof result>('published', { reportId }).then(response => active && setResult(response))
            .catch(failure => active && setError(failure.message || 'Report could not be opened.'));
        const escape = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
        if (!inline)
            document.addEventListener('keydown', escape);
        return () => { active = false; document.removeEventListener('keydown', escape); };
    }, [reportId]);
    return <div className={`${styles.panel} mx-auto ${inline ? '' : 'my-4'} max-w-5xl`}>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <p className={styles.muted}>{result ? `${result.serviceName} · ${result.deliveredBy} · ${formatLocalDateTime(result.deliveredAt)}` : 'Delivered report'}</p>
                <div className="flex gap-2">{result && <button type="button" className={styles.secondary}
                    onClick={() => printRenderedReport(result.report.title, body.current)}>Print / Save PDF</button>}
                    <button autoFocus={!inline} type="button" className={styles.secondary} onClick={onClose}>{inline ? 'Back to Intelligence Library' : 'Close report'}</button></div>
            </div>
            {error && <p role="alert" className={styles.error}>{error}</p>}
            {!result && !error && <p className={styles.muted}>Loading report…</p>}
            {result && <div ref={body}><ConsultantReportBody report={result.report} /></div>}
        </div>;
};

export const ConsultantReportViewer: React.FC<{ reportId: string; onClose: () => void }> = props =>
    createPortal(<div className="fixed inset-0 z-[1200] overflow-y-auto bg-slate-950/70 p-4" role="dialog" aria-modal="true" aria-label="Delivered report">
        <ConsultantReportView {...props} />
    </div>, document.body);

export const DeliveredReportProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [reportId, setReportId] = useState('');
    return <ReportNavigationContext.Provider value={setReportId}>{children}
        {reportId && <ConsultantReportViewer reportId={reportId} onClose={() => setReportId('')} />}
    </ReportNavigationContext.Provider>;
};

const DeliveredReportAttachment: React.FC<{ report: { id: string; title: string }; onOpened?: () => void }> = ({ report, onOpened }) => {
    const [open, setOpen] = useState(false);
    const openReport = React.useContext(ReportNavigationContext);
    return <><button type="button" onClick={() => {
        if (openReport) {
            onOpened?.();
            openReport(report.id);
        } else
            setOpen(true);
    }}
        className="mt-2 block rounded-lg border border-current px-3 py-2 text-left text-sm font-bold hover:opacity-80">Open report: {report.title}</button>
        {open && <ConsultantReportViewer reportId={report.id} onClose={() => setOpen(false)} />}</>;
};

export default DeliveredReportAttachment;
